import React from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Sheet } from '@pkg/components';
import { type SupportedWalletType, type WalletConnectionStep } from '@pkg/types';

import { type Account } from '@app/graphql/codegen/graphql';
import { useLegacyWalletStore } from '@app/store/zustand';

import { urls } from './ConnectWalletSheet.data';
import { FooterLinks, MainConnect, MainDisconnect, MainOverview } from './sections';

import { CreateWalletLink, FooterInterior } from './ConnectWalletSheet.style';

export interface ConnectWalletSheetProps {
    onClose?: () => void;
}

function getMainConnectKey(type: SupportedWalletType, step: WalletConnectionStep) {
    // Unique keys for `framer-motion` to animate between states.
    // We don't want animation between every `step`.
    function getStepSegment() {
        switch (step) {
            case 'tied-to-different-account':
            case 'missing-external-integration':
            case 'error': {
                return 'error';
            }

            case 'awaiting-external-action':
            case 'connecting': {
                return 'initial';
            }

            default: {
                return step;
            }
        }
    }

    return `MainConnect-${type}-${getStepSegment()}`;
}

export function ConnectWalletSheet({ onClose }: ConnectWalletSheetProps) {
    const [translate] = useTranslation('connectWallet');

    // TODO: We need to unify the `type` for wallets.
    const { showOverview, connecting, setConnecting, disconnecting, setDisconnecting, resetShowState } =
        useLegacyWalletStore();

    async function handleConnectDisconnectReset() {
        setConnecting(undefined);
        setDisconnecting(undefined);
    }

    // TODO: Disconnecting is still a work-in-progress
    async function handleDisconnect(wallet: Account) {
        setDisconnecting(wallet);
    }

    function handleClose() {
        resetShowState();
        onClose?.();
    }

    const backMarkup =
        (connecting || disconnecting) && showOverview ? (
            <Sheet.FixedCloseAction key="BackAction" back onAction={handleConnectDisconnectReset} />
        ) : null;

    const connectingMain = connecting ? (
        <MainConnect
            key={getMainConnectKey(connecting.type, connecting.step)}
            type={connecting.type}
            step={connecting.step}
        />
    ) : null;

    const disconnectingMain = disconnecting ? (
        <MainDisconnect
            key="MainDisconnect"
            walletAddress={disconnecting.address}
            onClose={handleConnectDisconnectReset}
        />
    ) : null;

    const overviewMain =
        connectingMain || disconnectingMain ? null : (
            <MainOverview key="MainOverview" onDisconnect={handleDisconnect} />
        );

    return (
        <Sheet gapless backdrop onClose={handleClose}>
            <AnimatePresence>
                {backMarkup}
                <Sheet.FixedCloseAction key="CloseAction" onAction={handleClose} />
            </AnimatePresence>

            <AnimatePresence mode="wait">
                {connectingMain || disconnectingMain}
                {overviewMain}
            </AnimatePresence>

            <Sheet.Footer extraPaddingBottom>
                <FooterInterior>
                    <CreateWalletLink to={urls.metamask} target="_blank" rel="noopener noreferrer">
                        {translate('sheet-footer-cta')}
                    </CreateWalletLink>

                    <FooterLinks />
                </FooterInterior>
            </Sheet.Footer>
        </Sheet>
    );
}
